/* eslint-disable jsx-a11y/anchor-is-valid */
import { useIntl, WalletLimits } from '@capsicum/common';
import { type LimitWithError } from '@capsicum/socios-core';

export type OnWalletErrorProps = {
	redirectToTopUp: () => void;
	type: WalletLimits;
};

export type OnWalletErrorShape = (args: OnWalletErrorProps) => null | string;

export type GetPurchaseErrorMessageType = (
	redirectToTopUp: () => void,
	error?: LimitWithError,
	/** Mandatory on MOB, make sure to handle MOB translations on useMOBWalletErrors in case they have complex chunks */
	onWalletError?: OnWalletErrorShape
) => string;

/**
 * Hook that generate the error message for the purchase amount on enter amount component
 * Used to avoid the messages to be platform dependent
 *
 * @returns {string} returns a translated error message
 */
export const useGetPurchaseErrorMessage = () => {
	const { $t } = useIntl();

	const getPurchaseErrorMessage: GetPurchaseErrorMessageType = (redirectToTopUp, error, onWalletError) => {
		if (error && error.type) {
			const walletError = onWalletError?.({ redirectToTopUp, type: error.type }) || null;

			if (walletError) return walletError;

			switch (error.type) {
				case WalletLimits.Minimal:
					return $t('payments.errors.minimumLimit');
				case WalletLimits.PspMaxAmount:
					return $t('payments.errors.minimumLimitForPaymentMethod');
				case WalletLimits.InsufficientBalance:
					return $t('payments.errors.maximumLimitWithoutPaymentMethod');
				case WalletLimits.RegulatoryRestriction:
					return $t('payments.errors.regulatoryLimit', {
						amount: error.limit.toString()
					});
				case WalletLimits.HoldingRestriction:
					return $t('payments.errors.holdingLimit', {
						amount: error.limit.toString()
					});
				case WalletLimits.FanTokenOfferingMinimal:
					return $t('payments.errors.fanTokenOfferingMinimalLimit', {
						amount: error.limit.toString()
					});
				case WalletLimits.FanTokenOfferingMaximal:
					return $t('payments.errors.fanTokenOfferingMaximalLimit', {
						amount: error.limit.toString()
					});
				case WalletLimits.DebitRestriction:
					return $t<JSX.Element>('payments.errors.NCWMaximalWithWalletBalance', {
						link: chunks => (
							<a data-testID="top-up-link-in-error-message" onClick={() => redirectToTopUp()}>
								{chunks}
							</a>
						)
					});
				case WalletLimits.FanTokenOfferingMaximalWithWalletBalance:
					return $t<JSX.Element>('payments.errors.fanTokenOfferingMaximalWithWalletBalance', {
						link: chunks => (
							<a data-testID="top-up-link-in-error-message" onClick={() => redirectToTopUp()}>
								{chunks}
							</a>
						)
					});
				default:
			}
		}

		//should never happen
		return '';
	};

	return getPurchaseErrorMessage;
};
